import React, { useEffect, useState } from 'react'

import MaintenanceTrend from 'pages/shared/MaintenanceTrend'

import Spinner from 'components/Spinner'

import Api from 'services/api'

import { DeviceMaintenanceTrendGroup, DeviceMetric, GroupMetric, Metric, Trend } from 'types/DeviceMetric'
import { Alert } from 'types/Alert'

interface List {
  id: string | number
  tag: string | null
  title: string
  color?: string
  showInTooltip?: boolean
  type: 'group' | 'metric'
}

type Props = {
  trend: Trend
  metricList: Metric[]
  groupList: GroupMetric[]
  list: List[],
  alerts: Alert[]
}

const PreviewField = ({ trend, metricList, groupList, list, alerts }: Props) => {
  const [metricAndGroupList, setMetricAndGroupList] = useState<(DeviceMaintenanceTrendGroup | DeviceMetric)[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const getDeviceMetricValues = async (metricId: string | undefined) => {
    if (!metricId) return []
    try {
      return await Api.DeviceMetrics.getTrendMetricValues(metricId)
    } catch (error) { // eslint-disable-line @typescript-eslint/no-unused-vars
      return []
    }
  }

  const addOverviewToList = async () => {
    const combineMetricAndGroup = await Promise.all(
      list.map(async (item, index) => {
        if (item.type === 'group') {
          const group = groupList.find((group) => group.groupId === item.id)
          if (group) {
            const metrics = await Promise.all(
              group.metrics.map(async (metricItem) => {
                const metric = metricList.find((metric) => metric.id === metricItem.metricId)

                return {
                  name: metric?.name || '',
                  color: metric?.color || '',
                  metricId: metric?.id || '',
                  sortPosition: metricItem.sortPosition,
                  values: await getDeviceMetricValues(metric?.id)
                }
              })
            )

            return {
              ...group,
              showInTooltip: item.showInTooltip || false,
              metrics,
              sortPosition: index
            } as DeviceMaintenanceTrendGroup
          }

          return undefined
        } else {
          const metric = metricList.find((metric) => metric.id === item.id)
          return {
            metricId: metric?.id || '',
            name: metric?.name || '',
            description: metric?.description || '',
            chart: metric?.charts[0] || undefined, // for preview setting charts to first chart
            callToAction: metric?.callToAction || [],
            showInTooltip: item.showInTooltip || false,
            unit: metric?.unit || '',
            sortPosition: index,
            color: metric?.color || '',
            values: await getDeviceMetricValues(metric?.id)
          } as DeviceMetric
        }
      })
    )
    setIsLoading(false)
    return combineMetricAndGroup.filter(Boolean) as (DeviceMaintenanceTrendGroup | DeviceMetric)[]
  }

  useEffect(() => {
    const fetchData = async () => {
      const data = await addOverviewToList()
      setMetricAndGroupList(data)
    }
    fetchData()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, metricList, groupList, alerts])

  if (isLoading) return <Spinner variant="page" />

  return (
    <MaintenanceTrend
      metricAndGroupList={metricAndGroupList}
      trend={trend}
      timeZone="UTC"
      showMetricSelection={false}
      sendAnalyticsEvents={false}
      alerts={alerts}
    />
  )
}

export default PreviewField
